import { useEffect, useState } from "react";
import { Navigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice } from "@/lib/api";
import { LOGO_URL } from "@/data/content";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartLine, Ticket, EnvelopeOpen, Users, SignOut, MagnifyingGlass, Tag } from "@phosphor-icons/react";
import { useSEO } from "@/lib/seo";
import PricingModule from "@/pages/admin/PricingModule";

function Stat({ icon: I, label, value, sub }) {
  return (
    <div className="bg-white border border-[#E0DDD6] p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="tcp-overline text-[#666460]">{label}</p>
        <I size={22} className="text-[#C29B62]" weight="duotone" />
      </div>
      <p className="font-serif text-4xl mt-3 text-[#1C1C1C] tracking-tighter">{value}</p>
      {sub && <p className="text-xs text-[#666460] mt-2">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState({ bookings: 0, leads: 0, subscribers: 0, revenue: 0, by_product: [] });
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [productFilter, setProductFilter] = useState("");

  useSEO({ title: "Admin Dashboard | TheCityPair", noindex: true });

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings"),
      api.get("/admin/leads"),
      api.get("/admin/subscribers"),
    ]).then(([s, b, l, n]) => {
      setStats(s.data); setBookings(b.data); setLeads(l.data); setSubs(n.data);
    }).catch(console.error);
  }, [user]);

  const search = async (val) => {
    setQ(val);
    const params = new URLSearchParams();
    if (val) params.set("q", val);
    if (productFilter) params.set("product_type", productFilter);
    const r = await api.get(`/admin/bookings${params.toString() ? `?${params.toString()}` : ""}`);
    setBookings(r.data);
  };

  const onProductFilter = async (val) => {
    setProductFilter(val);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (val) params.set("product_type", val);
    const r = await api.get(`/admin/bookings${params.toString() ? `?${params.toString()}` : ""}`);
    setBookings(r.data);
  };

  const exportCSV = (rows, name) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]).filter(k => typeof rows[0][k] !== "object");
    const csv = [headers.join(","), ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F0] grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className="bg-[#0A0A0B] text-[#F7F5F0] p-8 lg:min-h-screen" data-testid="admin-sidebar">
        <NavLink to="/" className="block mb-12" data-testid="admin-logo-link">
          <img src={LOGO_URL} alt="TheCityPair" className="h-20 w-auto object-contain -ml-2" />
        </NavLink>
        <p className="tcp-overline text-[#C29B62] mb-4">Control Room</p>
        <nav className="space-y-2 text-sm">
          <p className="text-[#A09E9A]">Signed in as</p>
          <p className="font-semibold">{user.email}</p>
        </nav>
        <button onClick={logout} className="mt-10 flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#A09E9A] hover:text-[#C29B62]" data-testid="logout-btn">
          <SignOut size={14} /> Sign Out
        </button>
      </aside>

      <main className="p-6 md:p-10">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#1C1C1C] mb-2">Dashboard</h1>
        <p className="text-sm text-[#666460] mb-10">Bookings, leads, and subscribers at a glance.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat icon={Ticket} label="Bookings" value={stats.bookings} />
          <Stat icon={ChartLine} label="Revenue" value={formatPrice(stats.revenue)} sub="All-time" />
          <Stat icon={EnvelopeOpen} label="Leads" value={stats.leads} />
          <Stat icon={Users} label="Subscribers" value={stats.subscribers} />
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-white border border-[#E0DDD6] rounded-none p-0 h-auto">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:bg-[#C29B62] data-[state=active]:text-[#0A0A0B] px-6 py-3">Bookings</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none data-[state=active]:bg-[#C29B62] data-[state=active]:text-[#0A0A0B] px-6 py-3">Leads</TabsTrigger>
            <TabsTrigger value="subs" data-testid="tab-subs" className="rounded-none data-[state=active]:bg-[#C29B62] data-[state=active]:text-[#0A0A0B] px-6 py-3">Subscribers</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-none data-[state=active]:bg-[#C29B62] data-[state=active]:text-[#0A0A0B] px-6 py-3">
              <Tag size={14} weight="duotone" className="inline mr-1.5" />
              Deals &amp; Pricing
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[280px]">
                <div className="relative flex-1 max-w-sm">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666460]" />
                  <input value={q} onChange={(e) => search(e.target.value)} placeholder="Search reference / email / route" className="tcp-input pl-9" data-testid="bookings-search" />
                </div>
                <select value={productFilter} onChange={(e) => onProductFilter(e.target.value)} className="tcp-input w-auto" data-testid="product-filter">
                  <option value="">All products</option>
                  <option value="flights">Flights</option>
                  <option value="hotels">Hotels</option>
                  <option value="cars">Car Rentals</option>
                  <option value="transfers">Transfers</option>
                  <option value="packages">Packages</option>
                  <option value="cruises">Cruises</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>
              <button onClick={() => exportCSV(bookings, "bookings")} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="export-bookings">Export CSV</button>
            </div>
            <div className="bg-white border border-[#E0DDD6] overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Reference</TableHead><TableHead>Product</TableHead><TableHead>Route</TableHead><TableHead>Date</TableHead><TableHead>Pax</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Total</TableHead>
                </TableRow></TableHeader>
                <TableBody data-testid="bookings-table">
                  {bookings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#666460] py-8">No bookings yet</TableCell></TableRow>}
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.reference}`}>
                      <TableCell className="font-mono text-xs">{b.reference}</TableCell>
                      <TableCell><span className="px-2 py-0.5 bg-[#F7F5F0] border border-[#E0DDD6] text-xs uppercase tracking-wider">{b.product_type || "flights"}</span></TableCell>
                      <TableCell>{b.origin} → {b.destination}</TableCell>
                      <TableCell>{b.depart_date}</TableCell>
                      <TableCell>{b.passengers?.length}</TableCell>
                      <TableCell>{b.contact_email}</TableCell>
                      <TableCell className="text-right font-semibold" style={{ color: "#C29B62" }}>{formatPrice(b.price_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="flex justify-end mb-4">
              <button onClick={() => exportCSV(leads, "leads")} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="export-leads">Export CSV</button>
            </div>
            <div className="bg-white border border-[#E0DDD6] overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Subject</TableHead><TableHead>Phone</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                <TableBody data-testid="leads-table">
                  {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#666460] py-8">No leads yet</TableCell></TableRow>}
                  {leads.map(l => (
                    <TableRow key={l.id}><TableCell>{l.name}</TableCell><TableCell>{l.email}</TableCell><TableCell>{l.subject}</TableCell><TableCell>{l.phone || "—"}</TableCell><TableCell className="text-xs text-[#666460]">{new Date(l.created_at).toLocaleString()}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="subs" className="mt-6">
            <div className="flex justify-end mb-4">
              <button onClick={() => exportCSV(subs, "subscribers")} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="export-subs">Export CSV</button>
            </div>
            <div className="bg-white border border-[#E0DDD6] overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                <TableBody data-testid="subs-table">
                  {subs.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-[#666460] py-8">No subscribers yet</TableCell></TableRow>}
                  {subs.map(s => (
                    <TableRow key={s.id}><TableCell>{s.email}</TableCell><TableCell className="text-xs text-[#666460]">{new Date(s.created_at).toLocaleString()}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <PricingModule />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
